// src/components/GlobalSearch.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, Package, ShoppingCart, Archive, Truck, DollarSign, Users, Clock, CreditCard, RefreshCw,
  LayoutDashboard, Wallet, Lock, Box, Barcode, Mail, UserCircle, Settings, FileText, ArrowRight,
} from 'lucide-react';
import ModalPortal from './ModalPortal';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { sanitizeSearchInput } from '../utils/helpers';

// All navigable pages with keywords for matching
const APP_PAGES = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, keywords: ['home', 'dashboard', 'overview', 'stats', 'main'], description: 'Overview & analytics' },
  { name: 'Inventory', path: '/inventory', icon: Package, keywords: ['inventory', 'items', 'stock', 'products', 'collection'], description: 'Manage your inventory' },
  { name: 'Sales', path: '/sales', icon: DollarSign, keywords: ['sales', 'sold', 'revenue', 'transactions'], description: 'Track your sales' },
  { name: 'Expenses', path: '/expenses', icon: Wallet, keywords: ['expenses', 'spending', 'costs', 'bills', 'purchases'], description: 'Track your expenses' },
  { name: 'Vault', path: '/vault', icon: Lock, keywords: ['vault', 'storage', 'archive', 'hold', 'saved'], description: 'Long-term storage' },
  { name: 'Packages', path: '/packages', icon: Box, keywords: ['packages', 'tracking', 'shipments', 'delivery', 'shipping'], description: 'Track your packages' },
  { name: 'Email Scraper', path: '/email-scraper', icon: Mail, keywords: ['email', 'scraper', 'scrape', 'scan', 'gmail', 'orders', 'import', 'email scraper'], description: 'Scan emails for orders' },
  { name: 'My Products', path: '/product-scanner', icon: Barcode, keywords: ['products', 'scanner', 'barcode', 'my products', 'catalog'], description: 'Product scanner & catalog' },
  { name: 'Renewals', path: '/renewals', icon: RefreshCw, keywords: ['renewals', 'recurring', 'subscriptions', 'monthly', 'annual'], description: 'Recurring expenses' },
  { name: 'Buyers', path: '/buyers', icon: Users, keywords: ['buyers', 'customers', 'clients', 'contacts'], description: 'Manage your buyers' },
  { name: 'Account', path: '/account', icon: UserCircle, keywords: ['account', 'profile', 'user', 'my account', 'billing'], description: 'Your account settings' },
  { name: 'Settings', path: '/settings', icon: Settings, keywords: ['settings', 'preferences', 'config', 'configuration', 'options'], description: 'App preferences' },
  { name: 'Reports', path: '/reports', icon: FileText, keywords: ['reports', 'analytics', 'export', 'data', 'summary'], description: 'Reports & analytics' },
  { name: 'Archives', path: '/archives', icon: Archive, keywords: ['archives', 'archived', 'old', 'history', 'past'], description: 'Archived items' },
];

const SEARCH_CATEGORIES = [
  { id: 'all', label: 'All', icon: Search },
  { id: 'pages', label: 'Pages', icon: ArrowRight },
  { id: 'inventory', label: 'Inventory', icon: Package },
  { id: 'vault', label: 'Vault', icon: Archive },
  { id: 'sales', label: 'Sales', icon: DollarSign },
  { id: 'expenses', label: 'Expenses', icon: CreditCard },
  { id: 'renewals', label: 'Renewals', icon: RefreshCw },
  { id: 'packages', label: 'Packages', icon: Truck },
  { id: 'buyers', label: 'Buyers', icon: Users },
];

const TYPE_LABELS = {
  pages: 'Pages',
  inventory: 'Inventory',
  vault: 'Vault',
  sales: 'Sales',
  expenses: 'Expenses',
  renewals: 'Renewals',
  packages: 'Packages',
  buyers: 'Buyers',
};

const GlobalSearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { formatCurrency } = useSettings();
  const inputRef = useRef(null);
  const isMobile = useIsMobile();

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('nexus_recent_searches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        // Ignore
      }
    }
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  // Search function
  const performSearch = useCallback(async (searchQuery, searchCategory) => {
    if (!searchQuery.trim() || !currentUser) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    const searchResults = [];
    const q = sanitizeSearchInput(searchQuery.toLowerCase().trim());
    const qRaw = searchQuery.toLowerCase().trim();

    try {
      // Search Pages (client-side, instant) — always first
      if (searchCategory === 'all' || searchCategory === 'pages') {
        const pageResults = APP_PAGES.filter(page => {
          // Match against page name
          if (page.name.toLowerCase().includes(qRaw)) return true;
          // Match against keywords
          if (page.keywords.some(kw => kw.includes(qRaw) || qRaw.includes(kw))) return true;
          // Match against description
          if (page.description.toLowerCase().includes(qRaw)) return true;
          return false;
        }).map(page => ({
          id: `page-${page.path}`,
          type: 'pages',
          title: page.name,
          subtitle: page.description,
          icon: page.icon,
          path: page.path,
          isPage: true,
        }));
        searchResults.push(...pageResults);
      }

      // Search Inventory
      if (searchCategory === 'all' || searchCategory === 'inventory') {
        const { data: inventory } = await supabase
          .from('inventory')
          .select('id, name, sku, brand, purchase_price')
          .eq('user_id', currentUser.uid)
          .or(`name.ilike.%${q}%,sku.ilike.%${q}%,brand.ilike.%${q}%`)
          .limit(5);

        if (inventory) {
          searchResults.push(...inventory.map(item => ({
            id: item.id,
            type: 'inventory',
            title: item.name || 'Unnamed Item',
            subtitle: item.sku ? `SKU: ${item.sku}` : item.brand || '',
            value: item.purchase_price,
            icon: Package,
            path: `/view/${item.id}`,
          })));
        }
      }

      // Search Vault
      if (searchCategory === 'all' || searchCategory === 'vault') {
        const { data: vault } = await supabase
          .from('vault')
          .select('id, name, sku, brand, purchase_price')
          .eq('user_id', currentUser.uid)
          .or(`name.ilike.%${q}%,sku.ilike.%${q}%,brand.ilike.%${q}%`)
          .limit(5);

        if (vault) {
          searchResults.push(...vault.map(item => ({
            id: item.id,
            type: 'vault',
            title: item.name || 'Unnamed Item',
            subtitle: item.sku ? `SKU: ${item.sku}` : item.brand || '',
            value: item.purchase_price,
            icon: Archive,
            path: `/view-vault-item/${item.id}`,
          })));
        }
      }

      // Search Sales
      if (searchCategory === 'all' || searchCategory === 'sales') {
        const { data: sales } = await supabase
          .from('sales')
          .select('id, sale_name, buyer_name, sale_amount, sale_date')
          .eq('user_id', currentUser.uid)
          .or(`sale_name.ilike.%${q}%,buyer_name.ilike.%${q}%`)
          .limit(5);

        if (sales) {
          searchResults.push(...sales.map(sale => ({
            id: sale.id,
            type: 'sales',
            title: sale.sale_name || 'Unnamed Sale',
            subtitle: sale.buyer_name || '',
            value: sale.sale_amount,
            icon: DollarSign,
            path: '/sales',
          })));
        }
      }

      // Search Expenses (non-recurring only)
      if (searchCategory === 'all' || searchCategory === 'expenses') {
        const { data: expensesData } = await supabase
          .from('expenses')
          .select('id, description, category, place_of_purchase, price, tax, date')
          .eq('user_id', currentUser.uid)
          .neq('is_recurring', true)
          .or(`description.ilike.%${q}%,category.ilike.%${q}%,place_of_purchase.ilike.%${q}%`)
          .limit(5);

        if (expensesData) {
          searchResults.push(...expensesData.map(exp => ({
            id: exp.id,
            type: 'expenses',
            title: exp.description || 'Unnamed Expense',
            subtitle: [exp.category, exp.place_of_purchase].filter(Boolean).join(' \u2022 '),
            value: (Number(exp.price) || 0) + (Number(exp.tax) || 0),
            icon: CreditCard,
            path: '/expenses',
          })));
        }
      }

      // Search Renewals (recurring expenses)
      if (searchCategory === 'all' || searchCategory === 'renewals') {
        const { data: renewalsData } = await supabase
          .from('expenses')
          .select('id, description, category, place_of_purchase, price, tax, recurring_cycle, next_due_date')
          .eq('user_id', currentUser.uid)
          .eq('is_recurring', true)
          .or(`description.ilike.%${q}%,category.ilike.%${q}%,place_of_purchase.ilike.%${q}%`)
          .limit(5);

        if (renewalsData) {
          searchResults.push(...renewalsData.map(r => ({
            id: r.id,
            type: 'renewals',
            title: r.description || 'Unnamed Renewal',
            subtitle: [r.recurring_cycle, r.place_of_purchase].filter(Boolean).join(' \u2022 '),
            value: (Number(r.price) || 0) + (Number(r.tax) || 0),
            icon: RefreshCw,
            path: '/renewals',
          })));
        }
      }

      // Search Packages
      if (searchCategory === 'all' || searchCategory === 'packages') {
        const { data: packages } = await supabase
          .from('packages')
          .select('id, name, tracking_number, carrier, status')
          .eq('user_id', currentUser.uid)
          .or(`name.ilike.%${q}%,tracking_number.ilike.%${q}%`)
          .limit(5);

        if (packages) {
          searchResults.push(...packages.map(pkg => ({
            id: pkg.id,
            type: 'packages',
            title: pkg.name || pkg.tracking_number || 'Unnamed Package',
            subtitle: [pkg.carrier, pkg.status?.replace(/_/g, ' ')].filter(Boolean).join(' \u2022 '),
            icon: Truck,
            path: '/packages',
          })));
        }
      }

      // Search Buyers
      if (searchCategory === 'all' || searchCategory === 'buyers') {
        const { data: buyers } = await supabase
          .from('buyers')
          .select('id, display_name, emails, handles')
          .eq('user_id', currentUser.uid)
          .or(`display_name.ilike.%${q}%,display_name_lower.ilike.%${q}%`)
          .limit(5);

        if (buyers) {
          searchResults.push(...buyers.map(buyer => {
            const email = Array.isArray(buyer.emails) && buyer.emails.length > 0 ? buyer.emails[0] : '';
            const platform = Array.isArray(buyer.handles) && buyer.handles.length > 0 ? buyer.handles[0]?.platform : '';
            return {
              id: buyer.id,
              type: 'buyers',
              title: buyer.display_name || 'Unnamed Buyer',
              subtitle: email || platform || '',
              icon: Users,
              path: '/buyers',
            };
          }));
        }
      }

      setResults(searchResults);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query, category);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, category, performSearch]);

  // Build flat list for keyboard nav (accounts for group headers)
  const flatResults = results;

  // Handle result selection
  const handleSelect = (result) => {
    // Save to recent searches
    const newRecent = [
      { query, category, timestamp: Date.now() },
      ...recentSearches.filter(r => r.query !== query).slice(0, 4),
    ];
    setRecentSearches(newRecent);
    localStorage.setItem('nexus_recent_searches', JSON.stringify(newRecent));

    navigate(result.path);
    onClose();
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Group results by type for "All" mode
  const renderResults = () => {
    if (category !== 'all' || results.length === 0) {
      // Single-category: render flat list
      return results.map((result, index) => (
        <ResultRow
          key={`${result.type}-${result.id}`}
          result={result}
          isSelected={selectedIndex === index}
          onSelect={handleSelect}
          formatCurrency={formatCurrency}
        />
      ));
    }

    // "All" mode: group by type with section headers
    const grouped = {};
    const typeOrder = ['pages', 'inventory', 'vault', 'sales', 'expenses', 'renewals', 'packages', 'buyers'];
    results.forEach(r => {
      if (!grouped[r.type]) grouped[r.type] = [];
      grouped[r.type].push(r);
    });

    const elements = [];
    let runningIndex = 0;
    typeOrder.forEach(type => {
      const items = grouped[type];
      if (!items || items.length === 0) return;
      elements.push(
        <p key={`header-${type}`} className="px-4 py-1.5 text-[10px] uppercase tracking-wide font-medium" style={{ color: 'var(--color-text-muted)' }}>
          {TYPE_LABELS[type]} ({items.length})
        </p>
      );
      items.forEach((result) => {
        const idx = runningIndex++;
        elements.push(
          <ResultRow
            key={`${result.type}-${result.id}`}
            result={result}
            isSelected={selectedIndex === idx}
            onSelect={handleSelect}
            formatCurrency={formatCurrency}
          />
        );
      });
    });
    return elements;
  };

  if (!open) return null;

  // ─── Shared pieces ───
  const searchInput = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
      <Search style={{ width: '20px', height: '20px', flexShrink: 0, color: 'var(--color-text-muted)' }} />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search everything..."
        style={{ flex: 1, background: 'transparent', fontSize: '14px', outline: 'none', border: 'none', color: 'var(--color-text)' }}
      />
      {query && (
        <button onClick={() => setQuery('')} style={{ padding: '4px', color: 'var(--color-text-muted)' }}>
          <X style={{ width: '16px', height: '16px' }} />
        </button>
      )}
      <button onClick={onClose} style={{ padding: '4px', color: 'var(--color-text-muted)' }}>
        <X style={{ width: '20px', height: '20px' }} />
      </button>
    </div>
  );

  const categoryTabs = (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px',
        overflowX: 'auto', flexShrink: 0, borderBottom: '1px solid var(--color-border)',
        WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none',
      }}
    >
      {SEARCH_CATEGORIES.map((cat) => (
        <button
          key={cat.id}
          onClick={() => setCategory(cat.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
            borderRadius: '8px', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0,
            border: 'none', cursor: 'pointer', transition: 'background 0.15s',
            ...(category === cat.id
              ? { backgroundColor: 'rgb(var(--accent-primary))', color: '#fff' }
              : { backgroundColor: 'transparent', color: 'var(--color-text-muted)' }),
          }}
        >
          <cat.icon style={{ width: '14px', height: '14px' }} />
          {cat.label}
        </button>
      ))}
    </div>
  );

  const resultsContent = isLoading ? (
    <div style={{ padding: '32px', textAlign: 'center' }}>
      <div style={{ width: '24px', height: '24px', margin: '0 auto', border: '2px solid var(--color-border)', borderTopColor: 'var(--color-text-muted)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-text-muted)' }}>Searching...</p>
    </div>
  ) : results.length > 0 ? (
    <div style={{ padding: '4px 0' }}>
      {renderResults()}
    </div>
  ) : query ? (
    <div style={{ padding: '32px', textAlign: 'center' }}>
      <Search style={{ width: '32px', height: '32px', margin: '0 auto', color: 'var(--color-border)' }} />
      <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--color-text-muted)' }}>No results found</p>
      <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', opacity: 0.7 }}>Try a different search term</p>
    </div>
  ) : recentSearches.length > 0 ? (
    <div style={{ padding: '8px 0' }}>
      <p style={{ padding: '4px 16px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, color: 'var(--color-text-muted)' }}>
        Recent Searches
      </p>
      {recentSearches.map((recent, index) => (
        <div
          key={index}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', color: 'var(--color-text-muted)' }}
        >
          <button onClick={() => setQuery(recent.query)} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textAlign: 'left' }}>
            <Clock style={{ width: '16px', height: '16px', flexShrink: 0 }} />
            <span style={{ fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{recent.query}</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const updated = recentSearches.filter((_, i) => i !== index);
              setRecentSearches(updated);
              localStorage.setItem('nexus_recent_searches', JSON.stringify(updated));
            }}
            style={{ padding: '4px', background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' }}
            title="Remove"
          >
            <X style={{ width: '14px', height: '14px' }} />
          </button>
        </div>
      ))}
    </div>
  ) : (
    <div style={{ padding: '32px', textAlign: 'center' }}>
      <Search style={{ width: '32px', height: '32px', margin: '0 auto', color: 'var(--color-border)' }} />
      <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--color-text-muted)' }}>Start typing to search</p>
      <p style={{ fontSize: '12px', marginTop: '4px', color: 'var(--color-text-muted)', opacity: 0.7 }}>
        Search pages, inventory, sales, expenses, and more
      </p>
    </div>
  );

  // ─── MOBILE: full-screen takeover ───
  if (isMobile) {
    return (
      <ModalPortal>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            backgroundColor: 'var(--color-bg)',
            display: 'flex',
            flexDirection: 'column',
          }}
          onKeyDown={handleKeyDown}
        >
          {searchInput}
          {categoryTabs}
          <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {resultsContent}
          </div>
        </div>
      </ModalPortal>
    );
  }

  // ─── DESKTOP: centered card ───
  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[1300] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[12vh]"
        onClick={onClose}
      >
        <div
          className="max-w-xl w-full mx-4 rounded-xl shadow-2xl overflow-hidden flex flex-col"
          style={{ backgroundColor: 'var(--color-card)', border: 'none', maxHeight: '70vh' }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--color-border)' }}>
            <Search className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search everything..."
              className="flex-1 bg-transparent text-sm outline-none"
              style={{ color: 'var(--color-text)' }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 rounded transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="px-1.5 py-0.5 text-[10px] font-mono rounded" style={{ backgroundColor: 'var(--color-card-hover)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
              ESC
            </kbd>
          </div>

          {/* Category Tabs */}
          <div
            className="flex items-center gap-1.5 px-3 py-2 overflow-x-auto flex-shrink-0"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            {SEARCH_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0"
                style={category === cat.id ? {
                  backgroundColor: 'rgb(var(--accent-primary))',
                  color: '#fff',
                } : {
                  color: 'var(--color-text-muted)',
                }}
              >
                <cat.icon className="w-3.5 h-3.5" />
                {cat.label}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
            {resultsContent}
          </div>

          {/* Footer with keyboard shortcuts */}
          <div className="px-4 py-2 text-[10px] flex-shrink-0" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-card-hover)', color: 'var(--color-text-muted)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>↑</kbd>
                  <kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>↓</kbd>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>↵</kbd>
                  select
                </span>
              </div>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>Ctrl</kbd>
                <kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>K</kbd>
                to open
              </span>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

/* ---------- Result Row Component ---------- */
const ResultRow = ({ result, isSelected, onSelect, formatCurrency }) => (
  <button
    onClick={() => onSelect(result)}
    className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors"
    style={{
      backgroundColor: isSelected ? 'var(--color-card-hover)' : 'transparent',
    }}
  >
    <div className="p-1.5 rounded-lg shrink-0" style={{
      backgroundColor: result.isPage ? 'rgb(var(--accent-primary) / 0.1)' : 'var(--color-card-hover)',
    }}>
      <result.icon className="w-4 h-4" style={{
        color: result.isPage ? 'rgb(var(--accent-primary))' : 'var(--color-text-muted)',
      }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text)' }}>
        {result.title}
      </p>
      {result.subtitle && (
        <p className="text-xs truncate flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
          <span className="truncate">{result.subtitle}</span>
          {!result.isPage && (
            <span className="text-[9px] uppercase tracking-wide opacity-50 shrink-0">{TYPE_LABELS[result.type]}</span>
          )}
        </p>
      )}
    </div>
    {result.isPage ? (
      <ArrowRight className="w-4 h-4 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
    ) : result.value != null ? (
      <span className="text-xs font-medium shrink-0" style={{ color: 'var(--color-text)' }}>
        {formatCurrency(result.value)}
      </span>
    ) : null}
  </button>
);

/**
 * Hook to manage global search modal with Ctrl+K shortcut.
 * Uses a custom DOM event ('global-search-open') so that any component
 * calling open() triggers the single modal instance rendered in MainApp.
 */
export const useGlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K to open search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    // Listen for the custom open event (fired by other components)
    const handleOpenEvent = () => setIsOpen(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('global-search-open', handleOpenEvent);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('global-search-open', handleOpenEvent);
    };
  }, []);

  return {
    isOpen,
    open: () => {
      // Dispatch event so the MainApp instance picks it up
      window.dispatchEvent(new Event('global-search-open'));
    },
    close: () => setIsOpen(false),
  };
};

export default GlobalSearchModal;
